/**
 * Custom Polygon Drawing Controller - handles custom polygon drawing with area and distance measurements
 * User selects points sequentially until closing the polygon, showing:
 * - Coordinates at each point
 * - Distance on each edge (in km)
 * - Area in center (Ha)
 */
import {Subject} from 'rxjs';
import {
  calculateAreaHa,
  calculateDistance,
  formatDistance,
  formatCoordinates
} from './drawing-calculations.util';
import {BaseDrawController} from './base-draw.controller';
import {TranslateService} from '@ngx-translate/core';

export class DrawCustomPolygonController extends BaseDrawController {
  private customPolygonDrawingEnabled = false;
  private currentPolygonPoints: any[] = [];
  private currentPolygonMarkers: any[] = [];
  private currentPolygonLabels: any[] = [];
  private currentPolygonLine: any = null;
  private previewLine: any = null;
  private drawingControlsContainer: any;

  // Real-time measurement labels
  private measurementLabels: any[] = [];
  private areaLabel: any = null;

  // Event handlers stored for cleanup
  private dblClickHandler: any;
  private contextMenuHandler: any;
  private keyDownHandler: any;

  // Event emitter for shape creation
  public shapeCreated$ = new Subject<{
    type: 'custom-polygon';
    coordinates: [number, number][];
    center: { lat: number; lng: number };
    area: number;
    layer: any;
  }>();

  private translateService: TranslateService;

  constructor(mapInstance: any, translateService: TranslateService) {
    super(mapInstance);
    this.translateService = translateService;
  }

  /**
   * Enable custom polygon drawing mode
   */
  public enable(): void {
    if (this.customPolygonDrawingEnabled) return;

    this.customPolygonDrawingEnabled = true;
    this.enableDrawingMode();
    (this.map as any).doubleClickZoom.disable();

    // Add drawing control buttons
    this.addDrawingControls();

    // Attach event listeners
    this.mapClickHandler = (e: any) => this.handlePolygonClick(e);
    this.mapMouseMoveHandler = (e: any) => this.handlePolygonMouseMove(e);
    this.dblClickHandler = (e: any) => {
      (window as any).L.DomEvent.stopPropagation(e);
      (window as any).L.DomEvent.preventDefault(e);
    };
    this.contextMenuHandler = (e: any) => this.handlePolygonRightClick(e);
    this.keyDownHandler = (e: any) => this.handlePolygonKeyDown(e);

    this.map.on('click', this.mapClickHandler);
    this.map.on('mousemove', this.mapMouseMoveHandler);
    this.map.on('dblclick', this.dblClickHandler);
    this.map.getContainer().addEventListener('contextmenu', this.contextMenuHandler);
    document.addEventListener('keydown', this.keyDownHandler);
  }

  /**
   * Disable custom polygon drawing mode
   */
  public disable(): void {
    this.customPolygonDrawingEnabled = false;
    this.cancelCurrentPolygon();
    this.removeDrawingControls();
    this.disableDrawingMode();
    (this.map as any).doubleClickZoom.enable();

    // Remove event listeners
    this.removeMapEventListeners([
      { event: 'click', handler: this.mapClickHandler },
      { event: 'mousemove', handler: this.mapMouseMoveHandler },
      { event: 'dblclick', handler: this.dblClickHandler }
    ]);

    this.removeDomEventListeners([
      { element: this.map.getContainer(), event: 'contextmenu', handler: this.contextMenuHandler },
      { element: document, event: 'keydown', handler: this.keyDownHandler }
    ]);

    this.mapClickHandler = null;
    this.mapMouseMoveHandler = null;
    this.dblClickHandler = null;
    this.contextMenuHandler = null;
    this.keyDownHandler = null;
  }

  /**
   * Check if custom polygon drawing is active
   */
  public override isActive(): boolean {
    return this.customPolygonDrawingEnabled;
  }

  /**
   * Add drawing control buttons (Undo and Cancel)
   */
  private addDrawingControls(): void {
    const controlContainer = document.createElement('div');
    controlContainer.className = 'leaflet-drawing-controls';
    controlContainer.style.cssText = `
      position: absolute;
      top: 80px;
      left: 10px;
      z-index: 1000;
      display: none;
      flex-direction: column;
      gap: 8px;
    `;

    // Create Undo button
    const undoButton = document.createElement('button');
    undoButton.className = 'leaflet-control-button drawing-undo-btn';
    undoButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M9 14L4 9l5-5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M20 20v-7a4 4 0 0 0-4-4H4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    undoButton.title = 'Undo Last Point (Ctrl+Z)';
    undoButton.style.cssText = `
      width: 40px;
      height: 40px;
      background: white;
      border: 2px solid #ccc;
      border-radius: 4px;
      cursor: pointer;
      display: none;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      transition: all 0.2s;
    `;
    undoButton.addEventListener('mouseover', () => {
      undoButton.style.background = '#f5f5f5';
      undoButton.style.borderColor = '#4CAF50';
    });
    undoButton.addEventListener('mouseout', () => {
      undoButton.style.background = 'white';
      undoButton.style.borderColor = '#ccc';
    });
    undoButton.onclick = (e) => {
      e.stopPropagation();
      this.undoLastPoint();
    };

    // Create Cancel button
    const cancelButton = document.createElement('button');
    cancelButton.className = 'leaflet-control-button drawing-cancel-btn';
    cancelButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <line x1="18" y1="6" x2="6" y2="18" stroke-width="2" stroke-linecap="round"/>
        <line x1="6" y1="6" x2="18" y2="18" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
    cancelButton.title = 'Cancel Drawing (Esc)';
    cancelButton.style.cssText = `
      width: 40px;
      height: 40px;
      background: white;
      border: 2px solid #ccc;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      transition: all 0.2s;
    `;
    cancelButton.addEventListener('mouseover', () => {
      cancelButton.style.background = '#f5f5f5';
      cancelButton.style.borderColor = '#f44336';
    });
    cancelButton.addEventListener('mouseout', () => {
      cancelButton.style.background = 'white';
      cancelButton.style.borderColor = '#ccc';
    });
    cancelButton.onclick = (e) => {
      e.stopPropagation();
      this.cancelCurrentPolygon();
      this.disable();
    };

    controlContainer.appendChild(undoButton);
    controlContainer.appendChild(cancelButton);

    this.map.getContainer().appendChild(controlContainer);
    this.drawingControlsContainer = controlContainer;
  }

  /**
   * Remove drawing control buttons
   */
  private removeDrawingControls(): void {
    if (this.drawingControlsContainer && this.drawingControlsContainer.parentNode) {
      this.drawingControlsContainer.parentNode.removeChild(this.drawingControlsContainer);
      this.drawingControlsContainer = null;
    }
  }

  /**
   * Handle polygon click to add points
   */
  private handlePolygonClick(e: any): void {
    if (!this.customPolygonDrawingEnabled) return;
    (window as any).L.DomEvent.stopPropagation(e);

    if (this.currentPolygonPoints.length >= 3 && this.currentPolygonPoints[0]) {
      const firstPointPx = this.map.latLngToContainerPoint(this.currentPolygonPoints[0]);
      const cursorPx = this.map.latLngToContainerPoint(e.latlng);
      const isCloseToStart = firstPointPx.distanceTo(cursorPx) <= 12;

      if (isCloseToStart) {
        this.finishPolygonDrawing();
        return;
      }
    }

    this.addPolygonPoint(e.latlng);
  }

  /**
   * Handle polygon right-click to undo last point
   */
  private handlePolygonRightClick(e: any): void {
    if (!this.customPolygonDrawingEnabled) return;
    e.preventDefault();
    this.undoLastPoint();
  }

  /**
   * Handle keyboard shortcuts during polygon drawing
   */
  private handlePolygonKeyDown(e: any): void {
    if (!this.customPolygonDrawingEnabled) return;

    if (e.key === 'Escape') {
      this.cancelCurrentPolygon();
      this.disable();
    }
    if ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.undoLastPoint();
    }
    if (e.key === 'Enter' && this.currentPolygonPoints.length >= 3) {
      this.finishPolygonDrawing();
    }
  }

  /**
   * Handle polygon mouse move for interactive preview
   */
  private handlePolygonMouseMove(e: any): void {
    if (!this.customPolygonDrawingEnabled || this.currentPolygonPoints.length === 0) {
      this.map.getContainer().style.cursor = 'crosshair';
      const firstMarker = this.currentPolygonMarkers[0];
      if (firstMarker) {
        firstMarker.setStyle({
          radius: 7,
          fillColor: '#2196F3',
          fillOpacity: 1,
          color: '#fff',
          weight: 2
        });
        firstMarker.closeTooltip();
      }
      return;
    }

    const canClosePolygon = this.currentPolygonPoints.length >= 3 && this.currentPolygonPoints[0];
    let shouldHighlightStart = false;

    if (canClosePolygon) {
      const firstPointPx = this.map.latLngToContainerPoint(this.currentPolygonPoints[0]);
      const cursorPx = this.map.latLngToContainerPoint(e.latlng);
      shouldHighlightStart = firstPointPx.distanceTo(cursorPx) <= 12;
    }

    this.map.getContainer().style.cursor = shouldHighlightStart ? 'pointer' : 'crosshair';
    const firstMarker = this.currentPolygonMarkers[0];
    if (firstMarker) {
      firstMarker.setStyle({
        radius: shouldHighlightStart ? 10 : 7,
        fillColor: shouldHighlightStart ? '#FF9800' : '#2196F3',
        fillOpacity: 1,
        color: '#fff',
        weight: shouldHighlightStart ? 3 : 2
      });

      if (shouldHighlightStart) {
        firstMarker
          .bindTooltip(this.translateService.instant('DRAW_CLOSE_POLYGON_HINT'), {permanent: false, direction: 'top'})
          .openTooltip();
      } else {
        firstMarker.closeTooltip();
      }
    }

    const previewPoints = [...this.currentPolygonPoints, e.latlng];

    if (this.previewLine) {
      this.map.removeLayer(this.previewLine);
    }

    if (this.currentPolygonPoints.length >= 2) {
      this.previewLine = (window as any).L.polygon(previewPoints, {
        color: '#2196F3',
        weight: 2,
        opacity: 0.6,
        fill: true,
        fillColor: '#2196F3',
        fillOpacity: 0.2,
        dashArray: '5, 5',
        interactive: false
      }).addTo(this.map);

      // Update real-time measurements
      this.updateRealTimeMeasurements(previewPoints);
    } else if (this.currentPolygonPoints.length === 1) {
      // Show distance from first point to cursor
      this.previewLine = (window as any).L.polyline(previewPoints, {
        color: '#2196F3',
        weight: 2,
        opacity: 0.6,
        dashArray: '5, 5',
        interactive: false
      }).addTo(this.map);

      // Show distance label
      this.updateDistanceLabel(this.currentPolygonPoints[0], e.latlng);
    }
  }

  /**
   * Update real-time measurement labels during drawing
   */
  private updateRealTimeMeasurements(points: any[]): void {
    const L = (window as any).L;

    // Clear old measurement labels (but keep existing point labels)
    this.clearDynamicMeasurementLabels();

    if (points.length < 3) return;

    // Calculate area
    const areaHa = calculateAreaHa(points);

    // Calculate centroid for area label
    let centerLat = 0;
    let centerLng = 0;
    points.forEach((p: any) => {
      centerLat += p.lat;
      centerLng += p.lng;
    });
    centerLat /= points.length;
    centerLng /= points.length;

    // Create area label at center
    const areaLabelContent = `
      <div style="
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 8px 14px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 15px;
        font-weight: 600;
        white-space: nowrap;
        text-align: center;
        border: 2px solid rgba(255,255,255,0.3);
      ">
        ${areaHa.toFixed(2)} ${this.translateService.instant('Ha')}
      </div>
    `;

    const areaLabelIcon = L.divIcon({
      className: 'measurement-label area-label',
      html: areaLabelContent,
      iconSize: [120, 50],
      iconAnchor: [60, 25]
    });

    this.areaLabel = L.marker([centerLat, centerLng], {
      icon: areaLabelIcon,
      interactive: false,
      zIndexOffset: 2000
    }).addTo(this.map);

    this.measurementLabels.push(this.areaLabel);

    // Add distance labels on each edge
    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];

      const distance = calculateDistance(p1.lat, p1.lng, p2.lat, p2.lng);
      const midLat = (p1.lat + p2.lat) / 2;
      const midLng = (p1.lng + p2.lng) / 2;

      const edgeLabelContent = `
        <div style="
          background: rgba(76, 175, 80, 0.95);
          color: white;
          padding: 3px 8px;
          border-radius: 4px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          font-family: 'Segoe UI', Arial, sans-serif;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
          border: 1px solid rgba(255,255,255,0.4);
        ">
          ${formatDistance(distance, this.translateService)}
        </div>
      `;

      const edgeLabelIcon = L.divIcon({
        className: 'measurement-label edge-label',
        html: edgeLabelContent,
        iconSize: [80, 20],
        iconAnchor: [40, 10]
      });

      const edgeLabel = L.marker([midLat, midLng], {
        icon: edgeLabelIcon,
        interactive: false,
        zIndexOffset: 1500
      }).addTo(this.map);

      this.measurementLabels.push(edgeLabel);
    }
  }

  /**
   * Update distance label for single point drawing
   */
  private updateDistanceLabel(point1: any, point2: any): void {
    const L = (window as any).L;

    // Clear old dynamic labels
    this.clearDynamicMeasurementLabels();

    const distance = calculateDistance(point1.lat, point1.lng, point2.lat, point2.lng);
    const midLat = (point1.lat + point2.lat) / 2;
    const midLng = (point1.lng + point2.lng) / 2;

    const distanceLabelContent = `
      <div style="
        background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
        color: white;
        padding: 6px 12px;
        border-radius: 5px;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        border: 2px solid rgba(255,255,255,0.3);
      ">
        📏 ${formatDistance(distance, this.translateService)}
      </div>
    `;

    const distanceLabelIcon = L.divIcon({
      className: 'measurement-label distance-label',
      html: distanceLabelContent,
      iconSize: [100, 30],
      iconAnchor: [50, 15]
    });

    const distanceLabel = L.marker([midLat, midLng], {
      icon: distanceLabelIcon,
      interactive: false,
      zIndexOffset: 2000
    }).addTo(this.map);

    this.measurementLabels.push(distanceLabel);
  }

  /**
   * Clear dynamic measurement labels (area, perimeter, edge distances)
   * Keep coordinate labels at vertices
   */
  private clearDynamicMeasurementLabels(): void {
    this.measurementLabels.forEach(label => {
      if (label && this.map.hasLayer(label)) {
        this.map.removeLayer(label);
      }
    });
    this.measurementLabels = [];
    this.areaLabel = null;
  }

  /**
   * Clear all measurement labels including coordinate labels
   */
  private clearAllMeasurementLabels(): void {
    this.clearDynamicMeasurementLabels();
    this.currentPolygonLabels.forEach(label => {
      if (label && this.map.hasLayer(label)) {
        this.map.removeLayer(label);
      }
    });
    this.currentPolygonLabels = [];
  }

  /**
   * Add a point to the current polygon
   */
  private addPolygonPoint(latlng: any): void {
    this.currentPolygonPoints.push(latlng);

    const L = (window as any).L;
    const isFirstPoint = this.currentPolygonMarkers.length === 0;

    // Add marker
    const marker = L.circleMarker(latlng, {
      radius: isFirstPoint ? 7 : 5,
      fillColor: isFirstPoint ? '#2196F3' : '#4CAF50',
      fillOpacity: 1,
      color: '#fff',
      weight: 2,
      interactive: true
    }).addTo(this.map);

    marker.bindTooltip(
      isFirstPoint
        ? this.translateService.instant('DRAW_CLOSE_POLYGON_HINT')
        : `Point ${this.currentPolygonMarkers.length + 1}`,
      {
      permanent: false,
      direction: 'top'
      }
    );

    this.currentPolygonMarkers.push(marker);
    this.map.getContainer().style.cursor = 'crosshair';
    const firstMarker = this.currentPolygonMarkers[0];
    if (firstMarker) {
      firstMarker.setStyle({
        radius: 7,
        fillColor: '#2196F3',
        fillOpacity: 1,
        color: '#fff',
        weight: 2
      });
      firstMarker.closeTooltip();
    }

    // Add coordinate label
    const coordLabelContent = `
      <div style="
        background: rgba(33, 150, 243, 0.95);
        color: white;
        padding: 4px 8px;
        border-radius: 3px;
        box-shadow: 0 2px 5px rgba(0,0,0,0.2);
        font-family: 'Consolas', 'Courier New', monospace;
        font-size: 10px;
        font-weight: 500;
        white-space: nowrap;
        border: 1px solid rgba(255,255,255,0.4);
      ">
        <div style="opacity: 0.9;">${formatCoordinates(latlng.lat, latlng.lng)}</div>
      </div>
    `;

    const coordLabelIcon = L.divIcon({
      className: 'measurement-label coord-label',
      html: coordLabelContent,
      iconSize: [140, 20],
      iconAnchor: [-15, 10]
    });

    const coordLabel = L.marker([latlng.lat, latlng.lng], {
      icon: coordLabelIcon,
      interactive: false,
      zIndexOffset: 1000
    }).addTo(this.map);

    this.currentPolygonLabels.push(coordLabel);

    // Update line
    if (this.currentPolygonPoints.length >= 2) {
      this.updatePolygonLine();
    }

    // Update measurements immediately after adding point
    if (this.currentPolygonPoints.length >= 3) {
      this.updateRealTimeMeasurements(this.currentPolygonPoints);
    }
  }

  /**
   * Update polygon line visualization
   */
  private updatePolygonLine(): void {
    if (this.currentPolygonLine) {
      this.map.removeLayer(this.currentPolygonLine);
    }

    this.currentPolygonLine = (window as any).L.polyline(this.currentPolygonPoints, {
      color: '#4CAF50',
      weight: 2,
      opacity: 0.8,
      dashArray: '5, 5',
      interactive: false
    }).addTo(this.map);
  }

  /**
   * Undo last point
   */
  private undoLastPoint(): void {
    if (this.currentPolygonPoints.length === 0) return;

    this.currentPolygonPoints.pop();

    if (this.currentPolygonMarkers.length > 0) {
      const lastMarker = this.currentPolygonMarkers.pop();
      this.map.removeLayer(lastMarker);
    }

    if (this.currentPolygonLabels.length > 0) {
      const lastLabel = this.currentPolygonLabels.pop();
      this.map.removeLayer(lastLabel);
    }

    if (this.currentPolygonPoints.length >= 2) {
      this.updatePolygonLine();
      if (this.currentPolygonPoints.length >= 3) {
        this.updateRealTimeMeasurements(this.currentPolygonPoints);
      } else {
        this.clearDynamicMeasurementLabels();
      }
    } else {
      if (this.currentPolygonLine) {
        this.map.removeLayer(this.currentPolygonLine);
        this.currentPolygonLine = null;
      }
      this.clearDynamicMeasurementLabels();
    }

    if (this.currentPolygonPoints.length < 3) {
      this.map.getContainer().style.cursor = 'crosshair';
      const firstMarker = this.currentPolygonMarkers[0];
      if (firstMarker) {
        firstMarker.setStyle({
          radius: 7,
          fillColor: '#2196F3',
          fillOpacity: 1,
          color: '#fff',
          weight: 2
        });
        firstMarker.closeTooltip();
      }
    }

  }

  /**
   * Finish polygon drawing and create final polygon
   */
  public finishPolygonDrawing(): void {
    if (this.currentPolygonPoints.length < 3) {
      this.cancelCurrentPolygon();
      return;
    }

    const L = (window as any).L;

    const finalPolygon = L.polygon(this.currentPolygonPoints, {
      color: '#2196F3',
      weight: 2,
      opacity: 0.9,
      fill: true,
      fillColor: '#2196F3',
      fillOpacity: 0.4,
      interactive: true,
      className: 'drawn-custom-polygon'
    }).addTo(this.map);

    // Calculate final area
    const areaHa = calculateAreaHa(this.currentPolygonPoints);

    // Calculate center
    let centerLat = 0;
    let centerLng = 0;
    this.currentPolygonPoints.forEach((p: any) => {
      centerLat += p.lat;
      centerLng += p.lng;
    });
    centerLat /= this.currentPolygonPoints.length;
    centerLng /= this.currentPolygonPoints.length;

    // Create permanent labels for the finished polygon
    this.createPermanentLabels(this.currentPolygonPoints, areaHa);

    // Store copies of markers and labels BEFORE clearing
    const finalMarkers = [...this.currentPolygonMarkers];
    const finalLabels = [...this.currentPolygonLabels, ...this.measurementLabels];

    this.shapeCreated$.next({
      type: 'custom-polygon',
      coordinates: this.currentPolygonPoints.map(point => [point.lat, point.lng]),
      center: {lat: centerLat, lng: centerLng},
      area: areaHa,
      layer: finalPolygon
    });

    // Add polygon to drawn items
    if (this.drawnItems) {
      this.drawnItems.addLayer(finalPolygon);
      // Add markers to drawn items (keep them visible)
      finalMarkers.forEach(m => this.drawnItems.addLayer(m));
      // Add labels to drawn items (keep them visible)
      finalLabels.forEach(l => this.drawnItems.addLayer(l));
    }

    // Store in allDrawnShapes for tracking
    if (!this.allDrawnShapes) {
      this.allDrawnShapes = [];
    }
    this.allDrawnShapes.push({
      layer: finalPolygon,
      markers: finalMarkers,
      labels: finalLabels
    });

    // Clear temporary drawing state without removing the labels
    this.clearDrawingState();
    this.disable();
  }

  /**
   * Create permanent labels for the finished polygon
   */
  private createPermanentLabels(points: any[], areaHa: number): void {
    const L = (window as any).L;

    // Clear any temporary measurement labels
    this.clearDynamicMeasurementLabels();

    // Calculate centroid for area label
    let centerLat = 0;
    let centerLng = 0;
    points.forEach((p: any) => {
      centerLat += p.lat;
      centerLng += p.lng;
    });
    centerLat /= points.length;
    centerLng /= points.length;

    // Create area label at center
    const areaLabelContent = `
      <div style="
        background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
        color: white;
        padding: 10px 16px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 15px;
        font-weight: 600;
        text-align: center;
        border: 2px solid rgba(255,255,255,0.3);
      ">
        ${areaHa.toFixed(2)} ${this.translateService.instant('Ha')}
      </div>
    `;

    const areaLabelIcon = L.divIcon({
      className: 'measurement-label area-label',
      html: areaLabelContent,
      iconSize: [120, 50],
      iconAnchor: [60, 25]
    });

    const areaLabel = L.marker([centerLat, centerLng], {
      icon: areaLabelIcon,
      interactive: false,
      zIndexOffset: 2000
    }).addTo(this.map);

    this.measurementLabels.push(areaLabel);

    // Add distance labels on each edge
    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];

      const distance = calculateDistance(p1.lat, p1.lng, p2.lat, p2.lng);
      const midLat = (p1.lat + p2.lat) / 2;
      const midLng = (p1.lng + p2.lng) / 2;

      const edgeLabelContent = `
        <div style="
          background: rgba(76, 175, 80, 0.95);
          color: white;
          padding: 3px 8px;
          border-radius: 4px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          font-family: 'Segoe UI', Arial, sans-serif;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
          border: 1px solid rgba(255,255,255,0.4);
        ">
          ${formatDistance(distance, this.translateService)}
        </div>
      `;

      const edgeLabelIcon = L.divIcon({
        className: 'measurement-label edge-label',
        html: edgeLabelContent,
        iconSize: [80, 20],
        iconAnchor: [40, 10]
      });

      const edgeLabel = L.marker([midLat, midLng], {
        icon: edgeLabelIcon,
        interactive: false,
        zIndexOffset: 1500
      }).addTo(this.map);

      this.measurementLabels.push(edgeLabel);
    }
  }

  /**
   * Clear drawing state without removing permanent labels
   */
  private clearDrawingState(): void {
    this.map.getContainer().style.cursor = 'crosshair';
    const firstMarker = this.currentPolygonMarkers[0];
    if (firstMarker) {
      firstMarker.setStyle({
        radius: 7,
        fillColor: '#2196F3',
        fillOpacity: 1,
        color: '#fff',
        weight: 2
      });
      firstMarker.closeTooltip();
    }

    // Remove the temporary drawing line
    if (this.currentPolygonLine) {
      this.map.removeLayer(this.currentPolygonLine);
      this.currentPolygonLine = null;
    }

    if (this.previewLine) {
      this.map.removeLayer(this.previewLine);
      this.previewLine = null;
    }

    // Reset arrays (labels and markers are now managed by allDrawnShapes)
    this.currentPolygonPoints = [];
    this.currentPolygonMarkers = [];
    this.currentPolygonLabels = [];
    this.measurementLabels = [];
  }

  /**
   * Cancel current polygon drawing
   */
  private cancelCurrentPolygon(): void {
    this.map.getContainer().style.cursor = 'crosshair';
    const firstMarker = this.currentPolygonMarkers[0];
    if (firstMarker) {
      firstMarker.setStyle({
        radius: 7,
        fillColor: '#2196F3',
        fillOpacity: 1,
        color: '#fff',
        weight: 2
      });
      firstMarker.closeTooltip();
    }

    this.currentPolygonMarkers.forEach(marker => {
      this.map.removeLayer(marker);
    });
    this.currentPolygonMarkers = [];

    if (this.currentPolygonLine) {
      this.map.removeLayer(this.currentPolygonLine);
      this.currentPolygonLine = null;
    }

    if (this.previewLine) {
      this.map.removeLayer(this.previewLine);
      this.previewLine = null;
    }

    // Clear all measurement labels
    this.clearAllMeasurementLabels();

    this.currentPolygonPoints = [];
  }

  /**
   * Get current polygon points
   */
  public getPolygonPoints(): any[] {
    return [...this.currentPolygonPoints];
  }

  /**
   * Clear all drawn shapes and their associated labels and markers
   */
  public clearAllShapes(): void {
    // Remove all shapes, markers, labels that are tracked in allDrawnShapes
    if (this.allDrawnShapes && Array.isArray(this.allDrawnShapes)) {
      this.allDrawnShapes.forEach(shape => {
        // Remove the layer
        if (shape.layer) {
          if (this.drawnItems && this.drawnItems.hasLayer(shape.layer)) {
            this.drawnItems.removeLayer(shape.layer);
          }
          if (this.map && this.map.hasLayer(shape.layer)) {
            this.map.removeLayer(shape.layer);
          }
        }

        // Remove all markers associated with THIS shape only
        if (shape.markers && Array.isArray(shape.markers)) {
          shape.markers.forEach((marker: any) => {
            if (this.drawnItems && this.drawnItems.hasLayer(marker)) {
              this.drawnItems.removeLayer(marker);
            }
            if (this.map && this.map.hasLayer(marker)) {
              this.map.removeLayer(marker);
            }
          });
        }

        // Remove all labels associated with THIS shape only
        if (shape.labels && Array.isArray(shape.labels)) {
          shape.labels.forEach((label: any) => {
            if (this.drawnItems && this.drawnItems.hasLayer(label)) {
              this.drawnItems.removeLayer(label);
            }
            if (this.map && this.map.hasLayer(label)) {
              this.map.removeLayer(label);
            }
          });
        }
      });

      // Clear the array
      this.allDrawnShapes = [];
    }

    // Cancel any ongoing drawing
    this.cancelCurrentPolygon();
  }

}
